import { type Table } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Button } from "./button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { useSearchParams } from "react-router";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  isSelectable: boolean;
  total: number;
}

export function DataTablePagination<TData>({
  table,
  isSelectable,
  total,
}: DataTablePaginationProps<TData>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRows = table.getFilteredSelectedRowModel().rows;

  const page = searchParams.get("page") ?? "1";
  const limit = searchParams.get("limit") ?? "10";
  const totalPages = Math.ceil(total / Number(limit));

  const handleSetSearchParams = (searchParams: URLSearchParams) => {
    setSearchParams(searchParams, { viewTransition: true });
  };

  return (
    <div className="flex items-center justify-between px-2">
      <div className="text-muted-foreground flex-1 text-sm">
        {isSelectable ? (
          <>
            {selectedRows.length} de {total} filas seleccionadas.
          </>
        ) : (
          <>Total de {total} filas.</>
        )}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Filas por página</p>
          <Select
            value={limit}
            onValueChange={(value) => {
              searchParams.set("limit", value);
              searchParams.set("page", "1");
              handleSetSearchParams(searchParams);
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={limit} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 25, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Página {page} de {totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={() => {
              searchParams.set("page", "1");
              handleSetSearchParams(searchParams);
            }}
            disabled={Number(page) === 1}
          >
            <span className="sr-only">Ir a la primera página</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => {
              searchParams.set("page", (Number(page) - 1).toString());
              handleSetSearchParams(searchParams);
            }}
            disabled={Number(page) === 1}
          >
            <span className="sr-only">Ir a la página anterior</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => {
              searchParams.set("page", (Number(page) + 1).toString());
              handleSetSearchParams(searchParams);
            }}
            disabled={Number(page) === totalPages}
          >
            <span className="sr-only">Ir a la página siguiente</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={() => {
              searchParams.set("page", totalPages.toString());
              handleSetSearchParams(searchParams);
            }}
            disabled={Number(page) === totalPages}
          >
            <span className="sr-only">Ir a la última página</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
